import { useCallback, useMemo } from 'react';

import 'dayjs/locale/de';
import 'dayjs/locale/en';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

import {
  localeAdvancedDateFormats,
  localeAdvancedDateTimeFormats,
  localeAdvancedTimeFormats,
  localeDateFormats,
  localeDateWithTimeFormats,
  localeTimeFormats,
  SupportedLanguages,
  territoryDateFormats,
} from '../i18n/constants';
import { getUserLanguage } from '../i18n/helpers';
import { useFlags } from '../services/launchDarkly';
import { useStores } from '../stores/useStores';
import { localeAmount } from '../utils/helpers';
import { devError } from '../utils/log';

export const useLocale = () => {
  const { userStore } = useStores();
  const { is_super_coin_territory } = useFlags();
  const { profile: userProfile } = userStore;

  const isGDPR = useMemo(() => {
    return is_super_coin_territory;
  }, [is_super_coin_territory]);

  const locale = useMemo(() => {
    return getUserLanguage(userProfile?.language);
  }, [userProfile]);

  const territoryDateFormat = useMemo(() => {
    const countryCode = userProfile?.territory?.countryCode;
    return countryCode ? territoryDateFormats[countryCode] : undefined;
  }, [userProfile]);

  const localeDateFormat = useMemo(() => {
    if (territoryDateFormat) {
      return territoryDateFormat.date;
    }
    return locale in localeDateFormats ? localeDateFormats[locale] : localeDateFormats[SupportedLanguages.EN];
  }, [locale, territoryDateFormat]);

  const localeDateWithTimeFormat = useMemo(() => {
    if (territoryDateFormat) {
      return territoryDateFormat.dateWithTime;
    }
    return locale in localeDateWithTimeFormats
      ? localeDateWithTimeFormats[locale]
      : localeDateWithTimeFormats[SupportedLanguages.EN];
  }, [locale, territoryDateFormat]);

  const localeTimeFormat = useMemo(() => {
    return locale in localeTimeFormats ? localeTimeFormats[locale] : localeTimeFormats[SupportedLanguages.EN];
  }, [locale]);

  const localeAdvancedDateFormat = useMemo(() => {
    if (territoryDateFormat) {
      return territoryDateFormat.advancedDate;
    }
    return locale in localeAdvancedDateFormats
      ? localeAdvancedDateFormats[locale]
      : localeAdvancedDateFormats[SupportedLanguages.EN];
  }, [locale, territoryDateFormat]);

  const localeAdvancedTimeFormat = useMemo(() => {
    return locale in localeAdvancedTimeFormats
      ? localeAdvancedTimeFormats[locale]
      : localeAdvancedTimeFormats[SupportedLanguages.EN];
  }, [locale]);

  const localeAdvancedDateTimeFormat = useMemo(() => {
    if (territoryDateFormat) {
      return territoryDateFormat.advancedDateTime;
    }
    return locale in localeAdvancedDateTimeFormats
      ? localeAdvancedDateTimeFormats[locale]
      : localeAdvancedDateTimeFormats[SupportedLanguages.EN];
  }, [locale, territoryDateFormat]);

  const localeCurrencySymbol = useCallback(() => {
    return userProfile?.currency?.symbol;
  }, [userProfile]);

  const localeCurrency = useCallback(
    (amount: number) => {
      const { currency, territory } = userProfile || {};
      try {
        if (currency?.code && territory?.country.code) {
          return localeAmount({
            amount,
            currency: currency.code,
            countryCode: territory.country.code,
          });
        } else {
          return amount.toLocaleString();
        }
      } catch (error) {
        devError('Error in localeCurrency', error);
        return amount.toLocaleString();
      }
    },
    [userProfile],
  );

  const formatDate = useCallback(
    (
      date: Date | string,
      options?: { withTime?: boolean; advancedFormat?: boolean; timeOnly?: boolean; format?: string },
    ) => {
      if (options?.format) {
        return dayjs(date).format(options.format);
      }
      let format = options?.advancedFormat ? localeAdvancedDateFormat : localeDateFormat;
      if (options?.withTime) {
        format = options.advancedFormat ? localeAdvancedDateTimeFormat : localeDateWithTimeFormat;
      }
      if (options?.timeOnly) {
        format = options.advancedFormat ? localeAdvancedTimeFormat : localeTimeFormat;
      }
      return dayjs(date).format(format);
    },
    [
      localeAdvancedDateFormat,
      localeAdvancedDateTimeFormat,
      localeAdvancedTimeFormat,
      localeDateFormat,
      localeDateWithTimeFormat,
      localeTimeFormat,
    ],
  );

  const localeDate = useCallback(
    (date: Date | string) => {
      return dayjs(date).locale(locale.toLowerCase());
    },
    [locale],
  );

  const getLocaleByLanguage = useCallback((language: SupportedLanguages) => {
    switch (language) {
      case SupportedLanguages.DE:
        return 'de-DE';
      case SupportedLanguages.EN:
        return 'en-US';
      // TODO: ES/FR target the US-Spanish and Canadian-French markets for now.
      // If es-ES / fr-FR markets are added, the locale must depend on the player's country, not only the language.
      case SupportedLanguages.ES:
        return 'es-US';
      case SupportedLanguages.FR:
        return 'fr-CA';
      default:
        return 'en-US';
    }
  }, []);

  return {
    localeCurrency,
    formatDate,
    localeDate,
    localeDateFormat,
    localeDateWithTimeFormat,
    isGDPR,
    localeCurrencySymbol,
    locale,
    getLocaleByLanguage,
  };
};
